import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, RotateCcw, Coins, AlertTriangle, Eye, EyeOff, ChevronRight, ChevronLeft, Shield, Flame, Loader2, Skull } from 'lucide-react';
import { CINEMATIC_KEYFRAMES, ScanlineOverlay, NoiseOverlay, GasParticles, ToxicDrips, SewerFrame, GlitchTitle, StatusBar, TotalFeesCounter } from './CinematicStyles';
import { useDemo } from '../../context/DemoContext';
import { toast } from 'sonner';
import OnChainReceipt, { fireOnChainToast } from '../OnChainReceipt';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CAUGHT_KEYFRAMES = `
  @keyframes impactFlash {
    0% { opacity: 1; background: white; }
    15% { opacity: 1; background: #ff0000; }
    30% { opacity: 0.8; background: #ff0000; }
    50% { opacity: 0.3; background: #ff0000; }
    100% { opacity: 0; background: transparent; }
  }
  @keyframes speedLinesRotate {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  @keyframes guardSlam {
    0% { transform: scale(3) rotate(-8deg); opacity: 0; filter: brightness(3) blur(15px); }
    20% { transform: scale(1.3) rotate(3deg); opacity: 1; filter: brightness(1.8) blur(0px); }
    35% { transform: scale(1.05) rotate(-1deg); filter: brightness(1); }
    50% { transform: scale(1.12) rotate(1.5deg); }
    65% { transform: scale(1.0) rotate(0deg); }
    100% { transform: scale(1.0) rotate(0deg); opacity: 1; filter: brightness(1); }
  }
  @keyframes bustedSlam {
    0% { transform: scale(5) rotate(-15deg); opacity: 0; letter-spacing: 0.8em; }
    25% { transform: scale(1.2) rotate(3deg); opacity: 1; letter-spacing: 0.15em; }
    40% { transform: scale(0.95) rotate(-1deg); }
    55% { transform: scale(1.05) rotate(0.5deg); }
    70% { transform: scale(1.0) rotate(0deg); letter-spacing: 0.2em; }
    100% { transform: scale(1.0) rotate(0deg); letter-spacing: 0.2em; opacity: 1; }
  }
  @keyframes mangaLines {
    0% { opacity: 0; transform: scale(0.3); }
    30% { opacity: 0.9; transform: scale(1.0); }
    100% { opacity: 0.15; transform: scale(1.2); }
  }
  @keyframes screenShake {
    0%, 100% { transform: translate(0,0); }
    10% { transform: translate(-8px, 6px); }
    20% { transform: translate(6px, -8px); }
    30% { transform: translate(-6px, 4px); }
    40% { transform: translate(4px, -4px); }
    50% { transform: translate(-4px, 6px); }
    60% { transform: translate(6px, -2px); }
    70% { transform: translate(-2px, 4px); }
    80% { transform: translate(4px, -6px); }
    90% { transform: translate(-4px, 2px); }
  }
  @keyframes vhsTrack {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }
  @keyframes alertPulse {
    0%, 100% { opacity: 0.7; text-shadow: 0 0 20px #ff0000, 0 0 40px #ff0000, 0 0 80px #ff000080; }
    50% { opacity: 1; text-shadow: 0 0 30px #ff0000, 0 0 60px #ff0000, 0 0 100px #ff0000; }
  }
  @keyframes exclamationBounce {
    0% { transform: scale(0) rotate(-30deg); opacity: 0; }
    40% { transform: scale(1.6) rotate(5deg); opacity: 1; }
    60% { transform: scale(0.9) rotate(-2deg); }
    80% { transform: scale(1.1) rotate(1deg); }
    100% { transform: scale(1.0) rotate(0deg); opacity: 1; }
  }
  @keyframes borderFlash {
    0%, 100% { border-color: rgba(255,0,0,0.8); box-shadow: inset 0 0 60px rgba(255,0,0,0.15); }
    50% { border-color: rgba(255,50,50,1); box-shadow: inset 0 0 100px rgba(255,0,0,0.3); }
  }
  @keyframes sovereignWake {
    0% { transform: scale(0.3); opacity: 0; filter: blur(20px) brightness(0.3); }
    35% { transform: scale(1.15); opacity: 1; filter: blur(0px) brightness(1.5); }
    55% { transform: scale(0.95); filter: brightness(1); }
    100% { transform: scale(1); opacity: 1; filter: brightness(1); }
  }
  @keyframes ironPupilDilate {
    0% { transform: scale(1.4); opacity: 0; }
    50% { transform: scale(0.7); opacity: 1; }
    100% { transform: scale(1); opacity: 1; }
  }
  @keyframes chainSwingLeft {
    0% { transform: translate(-220px, -120px) rotate(-90deg); opacity: 0; }
    50% { transform: translate(0, 0) rotate(-12deg); opacity: 1; }
    65% { transform: translate(0, 0) rotate(-6deg); }
    80% { transform: translate(0, 0) rotate(-10deg); }
    100% { transform: translate(0, 0) rotate(-8deg); opacity: 1; }
  }
  @keyframes chainSwingRight {
    0% { transform: translate(220px, -120px) rotate(90deg); opacity: 0; }
    50% { transform: translate(0, 0) rotate(12deg); opacity: 1; }
    65% { transform: translate(0, 0) rotate(6deg); }
    80% { transform: translate(0, 0) rotate(10deg); }
    100% { transform: translate(0, 0) rotate(8deg); opacity: 1; }
  }
  @keyframes runeRingSpin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  @keyframes runeRingSpinRev {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(-360deg); }
  }
  @keyframes emberSpark {
    0% { transform: translate(0, 0) scale(1); opacity: 1; }
    100% { transform: translate(var(--dx), var(--dy)) scale(0); opacity: 0; }
  }
  @keyframes stonecrackFlash {
    0% { opacity: 0; }
    20% { opacity: 0.6; }
    100% { opacity: 0.3; }
  }
`;

const DIFFICULTIES = {
  normal: {
    name: 'Normal',
    desc: 'Generous timing, forgiving locks',
    timer: 55,
    tolerances: [5, 3, 2],
    guardSpeeds: [2200, 1600, 1100],
    lookChance: [0.2, 0.3, 0.4],
    driftSpeed: 0.2,
    rewards: [3000, 8000, 18000],
    color: '#ff8c00',
  },
  hard: {
    name: 'Hard',
    desc: 'Tight locks, aggressive guard',
    timer: 40,
    tolerances: [3, 2, 1],
    guardSpeeds: [1800, 1200, 800],
    lookChance: [0.35, 0.45, 0.55],
    driftSpeed: 0.4,
    rewards: [5000, 15000, 30000],
    color: '#eab308',
  },
  nightmare: {
    name: 'Nightmare',
    desc: 'Near impossible. Legends only.',
    timer: 22,
    tolerances: [1.5, 0.8, 0.4],
    guardSpeeds: [900, 550, 350],
    lookChance: [0.55, 0.7, 0.88],
    driftSpeed: 1.2,
    rewards: [40000, 80000, 130000],
    color: '#ef4444',
  },
};

export default function SafeCracker({ onWin, stakerMultiplier = 1, walletAddress, connected }) {
  const { isLive } = useDemo();
  const [gameState, setGameState] = useState('menu');
  const [difficulty, setDifficulty] = useState('hard');
  const [currentStage, setCurrentStage] = useState(0);
  const [stageTargets, setStageTargets] = useState([]);
  const [dialValue, setDialValue] = useState(0);
  const [isGuardLooking, setIsGuardLooking] = useState(false);
  const [guardPosition, setGuardPosition] = useState(0);
  const [isCracking, setIsCracking] = useState(false);
  const [resistance, setResistance] = useState(0);
  const [timeLeft, setTimeLeft] = useState(40);
  const [totalReward, setTotalReward] = useState(0);
  const [showAlarm, setShowAlarm] = useState(false);
  const [stagesCompleted, setStagesCompleted] = useState([false, false, false]);
  const [crackingMessage, setCrackingMessage] = useState('');
  const [feesClaimed, setFeesClaimed] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [onchainReceipt, setOnchainReceipt] = useState(null);
  const [caughtPhase, setCaughtPhase] = useState('flash'); // 'flash' | 'results'
  const [dailyPlays, setDailyPlays] = useState(0);
  const [dailyLimit] = useState(5);
  const [dailyBlocked, setDailyBlocked] = useState(false);
  const sessionTokenRef = useRef(null);

  // Fetch daily plays remaining
  useEffect(() => {
    if (!walletAddress) return;
    fetch(`${API_URL}/api/safecracker/daily/${walletAddress}`)
      .then(r => r.json())
      .then(data => {
        setDailyPlays(data.daily_plays || 0);
        setDailyBlocked(data.blocked || false);
      })
      .catch(() => {});
  }, [walletAddress, gameState]);

  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragStartDialValue = useRef(0);
  const currentStageRef = useRef(currentStage);
  const isGuardLookingRef = useRef(isGuardLooking);

  useEffect(() => { currentStageRef.current = currentStage; }, [currentStage]);
  useEffect(() => {
    isGuardLookingRef.current = isGuardLooking;
    if (isGuardLooking && isDragging.current) {
      isDragging.current = false;
      setIsCracking(false);
      triggerAlarm('Guard spotted you!');
    }
  }, [isGuardLooking]);

  // Drive caught cinematic phases
  useEffect(() => {
    if (gameState === 'caught') {
      setCaughtPhase('flash');
      const t = setTimeout(() => setCaughtPhase('results'), 2400);
      return () => clearTimeout(t);
    }
  }, [gameState]);

  const diff = DIFFICULTIES[difficulty];

  const STAGES = [
    { name: 'Outer Ring', color: '#ff8c00', range: 100, tolerance: diff.tolerances[0], reward: diff.rewards[0], guardSpeed: diff.guardSpeeds[0] },
    { name: 'Inner Ring', color: '#eab308', range: 100, tolerance: diff.tolerances[1], reward: diff.rewards[1], guardSpeed: diff.guardSpeeds[1] },
    { name: 'Core Lock', color: '#ef4444', range: 100, tolerance: diff.tolerances[2], reward: diff.rewards[2], guardSpeed: diff.guardSpeeds[2] },
  ];

  const currentConfig = STAGES[currentStage];
  const expectedDirection = currentStage % 2 === 0 ? 'right' : 'left';

  const generateTargets = useCallback(() => [0,1,2].map(() => Math.floor(Math.random() * 40) + 40), []);

  const startGame = useCallback(async () => {
    // Request game session token from server
    if (walletAddress) {
      try {
        const res = await fetch(`${API_URL}/api/arcade/start-session`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ wallet_address: walletAddress, game_type: 'safe_cracker' }),
        });
        if (res.ok) { const data = await res.json(); sessionTokenRef.current = data.session_token; }
      } catch (err) { console.error('Session start failed:', err); }
    }
    setStageTargets(generateTargets());
    setCurrentStage(0);
    setDialValue(0);
    setGameState('playing');
    setTimeLeft(diff.timer);
    setTotalReward(0);
    setStagesCompleted([false, false, false]);
    setShowAlarm(false);
    setCrackingMessage('');
    setIsCracking(false);
    setFeesClaimed(false);
    setClaiming(false);
  }, [generateTargets, diff.timer, walletAddress]);

  // Guard patrol
  useEffect(() => {
    if (gameState !== 'playing') return;
    let lookAwayTimeout = null;
    const guardInterval = setInterval(() => {
      setGuardPosition(prev => Math.max(-50, Math.min(50, prev + (Math.random() > 0.5 ? 1 : -1) * 20)));
      const chance = diff.lookChance[currentStageRef.current] || 0.3;
      const newLooking = Math.random() < chance;
      setIsGuardLooking(newLooking);
      if (newLooking && isCracking) triggerAlarm('Guard spotted you!');
      // Guard glances then looks away — duration scales with difficulty
      if (newLooking) {
        const glanceDuration = Math.min(currentConfig.guardSpeed * 0.45, 900);
        lookAwayTimeout = setTimeout(() => setIsGuardLooking(false), glanceDuration);
      }
    }, currentConfig.guardSpeed);
    return () => { clearInterval(guardInterval); if (lookAwayTimeout) clearTimeout(lookAwayTimeout); };
  }, [gameState, currentStage, isCracking, currentConfig.guardSpeed, diff.lookChance]);

  // Timer
  useEffect(() => {
    if (gameState !== 'playing') return;
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) { setGameState('caught'); setCrackingMessage('Time expired'); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [gameState]);

  // Resistance
  useEffect(() => {
    if (gameState !== 'playing') { setResistance(0); return; }
    const target = stageTargets[currentStage];
    if (!target) return;
    const d = Math.abs(dialValue - target);
    const tol = currentConfig.tolerance * 3;
    setResistance(d <= tol ? Math.floor(100 * (1 - d / tol)) : 0);
  }, [dialValue, gameState, stageTargets, currentStage, currentConfig.tolerance]);

  // Dial drift
  useEffect(() => {
    if (gameState !== 'playing') return;
    const driftInterval = setInterval(() => {
      if (!isDragging.current && dialValue > 0) {
        setDialValue(prev => Math.max(0, prev - 0.4));
      }
    }, 50);
    return () => clearInterval(driftInterval);
  }, [gameState, dialValue]);

  const triggerAlarm = (message) => {
    setShowAlarm(true);
    setCrackingMessage(message);
    setIsCracking(false);
    setTimeout(() => { setShowAlarm(false); setCrackingMessage(''); setDialValue(0); }, 1500);
  };

  const checkTarget = useCallback(() => {
    if (gameState !== 'playing') return;
    const target = stageTargets[currentStage];
    const d = Math.abs(dialValue - target);
    if (d <= currentConfig.tolerance) {
      const newCompleted = [...stagesCompleted];
      newCompleted[currentStage] = true;
      setStagesCompleted(newCompleted);
      const stageReward = Math.floor(currentConfig.reward * stakerMultiplier);
      setTotalReward(prev => prev + stageReward);
      if (currentStage < 2) {
        setCrackingMessage(`${currentConfig.name} breached!`);
        setTimeout(() => { setCurrentStage(prev => prev + 1); setDialValue(0); setCrackingMessage(''); }, 1200);
      } else {
        const finalReward = totalReward + stageReward;
        setGameState('won');
        if (onWin) onWin(finalReward, { stages_completed: 3 });
      }
    }
  }, [gameState, dialValue, stageTargets, currentStage, currentConfig, stagesCompleted, stakerMultiplier, totalReward, onWin]);

  // Drag handlers
  const handleDialMouseDown = (e) => {
    if (gameState !== 'playing' || isGuardLooking) return;
    isDragging.current = true;
    setIsCracking(true);
    dragStartX.current = e.clientX || e.touches?.[0]?.clientX || 0;
    dragStartDialValue.current = dialValue;
  };

  const handleDialMouseMove = useCallback((e) => {
    if (!isDragging.current || gameState !== 'playing' || isGuardLookingRef.current) return;
    const clientX = e.clientX || e.touches?.[0]?.clientX || 0;
    const deltaX = clientX - dragStartX.current;
    const stage = currentStageRef.current;
    const shouldDragRight = stage % 2 === 0;
    if (shouldDragRight && deltaX < -30) { triggerAlarm('Wrong direction!'); isDragging.current = false; return; }
    if (!shouldDragRight && deltaX > 30) { triggerAlarm('Wrong direction!'); isDragging.current = false; return; }
    let newValue = shouldDragRight
      ? dragStartDialValue.current + (deltaX * 0.5)
      : dragStartDialValue.current + ((-deltaX) * 0.5);
    newValue = Math.max(0, Math.min(100, newValue));
    const target = stageTargets[stage];
    const tol = STAGES[stage].tolerance;
    if (newValue > target + tol * 8) { triggerAlarm('Anti-tamper activated!'); isDragging.current = false; return; }
    setDialValue(newValue);
  }, [gameState, stageTargets]);

  const handleDialMouseUp = useCallback(() => {
    if (isDragging.current) {
      isDragging.current = false;
      setIsCracking(false);
      checkTarget();
    }
  }, [checkTarget]);

  useEffect(() => {
    window.addEventListener('mousemove', handleDialMouseMove);
    window.addEventListener('mouseup', handleDialMouseUp);
    window.addEventListener('touchmove', handleDialMouseMove, { passive: false });
    window.addEventListener('touchend', handleDialMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleDialMouseMove);
      window.removeEventListener('mouseup', handleDialMouseUp);
      window.removeEventListener('touchmove', handleDialMouseMove);
      window.removeEventListener('touchend', handleDialMouseUp);
    };
  }, [handleDialMouseMove, handleDialMouseUp]);

  const claimFees = async () => {
    if (!walletAddress || feesClaimed || totalReward <= 0) return;
    setClaiming(true);
    try {
      const res = await fetch(`${API_URL}/api/safecracker/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          wallet_address: walletAddress,
          difficulty,
          stages_completed: stagesCompleted.filter(Boolean).length,
          session_token: sessionTokenRef.current,
          live_mode: !!isLive,
        }),
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        setFeesClaimed(true);
        sessionTokenRef.current = null;
        if (data?.onchain) {
          setOnchainReceipt(data.onchain);
          fireOnChainToast(toast, data.onchain);
        }
      }
    } catch (err) { console.error('Claim failed:', err); }
    setClaiming(false);
  };

  const dialRotation = (dialValue / 100) * 360;
  const completedCount = stagesCompleted.filter(Boolean).length;

  // ============ CINEMATIC MENU ============
  if (gameState === 'menu') {
    return (
      <div className="relative h-screen w-full overflow-hidden select-none" data-testid="safe-cracker-menu"
        style={{ background: 'radial-gradient(ellipse at 50% 70%, #0d0a05 0%, #0a0700 30%, #050300 100%)' }}>
        <style>{CINEMATIC_KEYFRAMES}</style>
        <ScanlineOverlay />
        <NoiseOverlay />
        <GasParticles count={18} color="molten" />
        <ToxicDrips count={8} color="#ff8c00" />
        <SewerFrame />

        {/* Vault silhouette background */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 0.06, scale: 1 }}
            transition={{ duration: 2 }}
            className="w-64 h-64 md:w-80 md:h-80 rounded-full border-[3px] border-orange-400/20"
            style={{ boxShadow: '0 0 60px rgba(255,140,0,0.05), inset 0 0 40px rgba(255,140,0,0.03)' }}
          >
            <div className="absolute inset-4 rounded-full border border-orange-400/10" />
            <div className="absolute inset-8 rounded-full border border-orange-400/5" />
            {/* Lock ticks */}
            {[...Array(24)].map((_, i) => (
              <div key={i} className="absolute w-px h-4" style={{
                left: '50%', top: '2px',
                transformOrigin: '50% calc(50% + 120px)',
                transform: `translateX(-50%) rotate(${i * 15}deg)`,
                background: `rgba(255,140,0,${i % 6 === 0 ? 0.15 : 0.05})`,
              }} />
            ))}
          </motion.div>
        </div>

        {/* Main Content */}
        <div className="relative z-20 flex flex-col items-center justify-center h-full px-4">

          <GlitchTitle text="VAULT HEIST" subtitle="breach the sewer vault" color="#ff8c00" />

          {/* Difficulty Selector */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.6 }}
            className="w-full max-w-md mt-8 space-y-2"
          >
            <p className="text-[10px] tracking-[0.3em] uppercase text-center mb-3"
               style={{ color: 'rgba(255,140,0,0.4)', fontFamily: "'Courier New', monospace" }}>
              // SELECT CLEARANCE LEVEL
            </p>
            {Object.entries(DIFFICULTIES).map(([key, d], idx) => {
              const isSelected = difficulty === key;
              const isNightmare = key === 'nightmare';
              const totalRewardDisplay = d.rewards.reduce((a, b) => a + b, 0);
              return (
                <motion.button
                  key={key}
                  onClick={() => setDifficulty(key)}
                  data-testid={`difficulty-${key}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.8 + idx * 0.15 }}
                  className="w-full relative overflow-hidden text-left"
                  style={{
                    background: isSelected
                      ? `linear-gradient(90deg, ${d.color}15 0%, transparent 100%)`
                      : 'rgba(0,0,0,0.3)',
                    border: `1px solid ${isSelected ? `${d.color}50` : 'rgba(255,255,255,0.04)'}`,
                    borderRadius: '4px',
                    padding: '12px 16px',
                    transition: 'all 0.3s ease',
                  }}
                >
                  {isSelected && (
                    <motion.div layoutId="sc-diff-indicator"
                      className="absolute left-0 top-0 bottom-0 w-0.5"
                      style={{ background: d.color, boxShadow: `0 0 8px ${d.color}` }}
                    />
                  )}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {isNightmare ? (
                        <Skull className="w-4 h-4" style={{ color: d.color, filter: `drop-shadow(0 0 6px ${d.color})` }} />
                      ) : (
                        <Lock className="w-4 h-4" style={{ color: isSelected ? d.color : 'rgba(255,255,255,0.2)' }} />
                      )}
                      <div>
                        <span className="font-bold text-sm tracking-wider" style={{
                          color: isSelected ? d.color : 'rgba(255,255,255,0.4)',
                          fontFamily: "'Courier New', monospace",
                          textShadow: isSelected ? `0 0 8px ${d.color}40` : 'none',
                        }}>
                          {d.name.toUpperCase()}
                        </span>
                        <p className="text-[10px] mt-0.5" style={{ color: 'rgba(255,255,255,0.25)', fontFamily: "'Courier New', monospace" }}>
                          {d.desc}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="text-xs font-bold" style={{
                        color: isSelected ? d.color : 'rgba(255,255,255,0.3)',
                        fontFamily: "'Courier New', monospace",
                      }}>
                        {totalRewardDisplay.toLocaleString()}
                      </span>
                      <p className="text-[9px]" style={{ color: 'rgba(255,255,255,0.15)' }}>max fees</p>
                    </div>
                  </div>
                </motion.button>
              );
            })}
          </motion.div>

          {/* Stage Blueprint */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2 }}
            className="flex gap-2 mt-6"
          >
            {STAGES.map((s, i) => (
              <div key={i} className="text-center px-3 py-2" style={{
                background: 'rgba(0,0,0,0.4)',
                border: `1px solid ${s.color}20`,
                borderRadius: '2px',
              }}>
                <div className="text-[9px] font-bold tracking-wider" style={{
                  color: `${s.color}80`,
                  fontFamily: "'Courier New', monospace",
                }}>{s.name.toUpperCase()}</div>
                <div className="text-base font-black mt-0.5" style={{ color: `${s.color}90` }}>
                  {s.reward.toLocaleString()}
                </div>
                <div className="text-[8px]" style={{ color: 'rgba(255,255,255,0.15)' }}>fees</div>
              </div>
            ))}
          </motion.div>

          {/* Daily Plays Counter */}
          {walletAddress && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.35 }}
              className="mt-4 text-center"
            >
              <span className="text-[10px] tracking-[0.2em] uppercase font-mono"
                style={{ color: dailyBlocked ? 'rgba(239,68,68,0.6)' : 'rgba(255,140,0,0.4)' }}>
                {dailyBlocked
                  ? '// DAILY HEIST LIMIT REACHED //'
                  : `// HEISTS TODAY: ${dailyPlays} / ${dailyLimit} //`}
              </span>
            </motion.div>
          )}

          {/* Start Button */}
          <motion.button
            onClick={dailyBlocked ? undefined : startGame}
            disabled={dailyBlocked}
            data-testid="start-heist-btn"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: dailyBlocked ? 0.4 : 1, y: 0 }}
            transition={{ delay: 1.5 }}
            className="mt-4 px-10 py-4 font-bold text-base tracking-[0.15em] uppercase relative overflow-hidden"
            style={{
              background: 'rgba(0,0,0,0.5)',
              border: `1px solid ${dailyBlocked ? 'rgba(239,68,68,0.3)' : 'rgba(255,140,0,0.4)'}`,
              color: dailyBlocked ? '#ef4444' : '#ff8c00',
              borderRadius: '2px',
              fontFamily: "'Courier New', monospace",
              animation: dailyBlocked ? 'none' : 'pulseGlow 3s ease-in-out infinite',
              cursor: dailyBlocked ? 'not-allowed' : 'pointer',
            }}
            whileHover={dailyBlocked ? {} : { scale: 1.02, borderColor: 'rgba(255,140,0,0.7)' }}
            whileTap={dailyBlocked ? {} : { scale: 0.98 }}
          >
            <span className="relative z-10 flex items-center gap-2">
              {dailyBlocked ? (
                <><Lock className="w-5 h-5" /> LOCKED UNTIL TOMORROW</>
              ) : (
                <><Lock className="w-5 h-5" /> INITIATE BREACH</>
              )}
            </span>
            {!dailyBlocked && (
              <motion.div
                className="absolute inset-0"
                style={{ background: 'linear-gradient(90deg, transparent, rgba(255,140,0,0.08), transparent)' }}
                animate={{ x: ['-100%', '100%'] }}
                transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
              />
            )}
          </motion.button>

          <StatusBar text="VAULT SECURITY: ACTIVE // 3 LAYERS DETECTED" color="#ff8c00" />
        </div>
      </div>
    );
  }

  // ============ PLAYING STATE ============
  if (gameState === 'playing') {
    return (
      <div className="relative h-screen w-full select-none overflow-hidden" data-testid="safe-cracker-game"
        style={{ background: 'radial-gradient(ellipse at 50% 70%, #0d0a05 0%, #0a0700 40%, #050300 100%)' }}>
        <style>{CINEMATIC_KEYFRAMES}</style>
        <ScanlineOverlay />
        <GasParticles count={10} color="molten" />

        {/* HUD - Top Bar — Centered */}
        <div className="absolute top-0 left-0 right-0 z-30 flex items-center justify-center py-3 px-2"
          style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.5) 60%, transparent 100%)' }}>
          <div className="flex items-center gap-3 md:gap-6">
            <div className="text-center">
              <span className="text-[8px] md:text-[9px] tracking-[0.2em] uppercase block font-bold" style={{ color: '#ffc870', fontFamily: "'Courier New', monospace", textShadow: '0 1px 2px rgba(0,0,0,0.9)' }}>TIME</span>
              <span className="text-xl md:text-2xl font-black tracking-wide"
                style={{
                  color: timeLeft <= 15 ? '#ef4444' : '#ff8c00',
                  fontFamily: "'Impact', 'Arial Black', sans-serif",
                  textShadow: timeLeft <= 15 ? '0 0 12px rgba(239,68,68,0.5)' : '0 0 12px rgba(255,140,0,0.35)',
                }}>
                {timeLeft}s
              </span>
            </div>
            <div className="w-px h-8" style={{ background: 'rgba(255,140,0,0.1)' }} />
            <div className="text-center">
              <span className="text-[8px] md:text-[9px] tracking-[0.2em] uppercase block" style={{ color: `${currentConfig.color}60`, fontFamily: "'Courier New', monospace" }}>STAGE</span>
              <span className="text-sm md:text-lg font-black tracking-wide" style={{
                color: currentConfig.color,
                fontFamily: "'Impact', 'Arial Black', sans-serif",
                textShadow: `0 0 10px ${currentConfig.color}40`,
              }}>
                {currentConfig.name.toUpperCase()}
              </span>
            </div>
            <div className="w-px h-8" style={{ background: 'rgba(255,140,0,0.1)' }} />
            <div className="text-center">
              <span className="text-[8px] md:text-[9px] tracking-[0.2em] uppercase block font-bold" style={{ color: '#ffc870', fontFamily: "'Courier New', monospace", textShadow: '0 1px 2px rgba(0,0,0,0.9)' }}>FEES</span>
              <span className="text-xl md:text-2xl font-black tracking-wide" style={{
                color: '#ff8c00',
                fontFamily: "'Impact', 'Arial Black', sans-serif",
                textShadow: '0 0 12px rgba(255,140,0,0.35)',
              }}>
                {totalReward.toLocaleString()}
              </span>
            </div>
          </div>
        </div>

        <div className="relative z-20 flex flex-col items-center justify-center h-full px-3 md:px-4" data-testid="safe-cracker-playing">
          {/* Stage indicators — brass plaques with carved layer names */}
          <div className="flex gap-1.5 md:gap-3 mb-4">
            {STAGES.map((stage, i) => {
              const active = i === currentStage;
              const done = stagesCompleted[i];
              return (
                <div key={i} className="relative px-3 md:px-5 py-1.5 md:py-2 text-center" style={{
                  background: done
                    ? `linear-gradient(180deg, rgba(20,12,4,0.95) 0%, ${stage.color}10 100%)`
                    : active
                      ? 'linear-gradient(180deg, rgba(20,12,4,0.95) 0%, rgba(8,4,2,0.95) 100%)'
                      : 'rgba(8,4,2,0.7)',
                  border: `1px solid ${done ? `${stage.color}70` : active ? `${stage.color}45` : 'rgba(140,100,40,0.18)'}`,
                  boxShadow: active
                    ? `inset 0 1px 1px rgba(220,170,80,0.18), inset 0 -1px 1px rgba(0,0,0,0.7), 0 0 14px ${stage.color}25`
                    : 'inset 0 1px 1px rgba(140,100,40,0.12), inset 0 -1px 1px rgba(0,0,0,0.6)',
                  borderRadius: 1,
                }} data-testid={`stage-dot-${i}`}>
                  {done && <Shield className="w-3 h-3 absolute -top-1 -right-1" style={{ color: stage.color, filter: `drop-shadow(0 0 4px ${stage.color})` }} />}
                  <span className="text-[9px] font-bold tracking-[0.18em]" style={{
                    color: done ? stage.color : active ? `${stage.color}` : 'rgba(160,120,50,0.4)',
                    fontFamily: "'Cinzel', serif",
                    textShadow: active || done ? `0 0 6px ${stage.color}55, 0 1px 1px rgba(0,0,0,0.9)` : 'none',
                  }}>{stage.name.toUpperCase()}</span>
                </div>
              );
            })}
          </div>

          {/* Guard Monitor */}
          <motion.div animate={{ x: guardPosition * 0.5 }} transition={{ type: 'spring', stiffness: 80, damping: 15 }}
            className="flex items-center gap-2 px-4 py-2 mb-4"
            style={{
              background: isGuardLooking ? 'rgba(239,68,68,0.08)' : 'rgba(0,0,0,0.4)',
              border: `1px solid ${isGuardLooking ? 'rgba(239,68,68,0.4)' : 'rgba(255,140,0,0.1)'}`,
              borderRadius: '2px', fontFamily: "'Courier New', monospace",
            }}>
            {isGuardLooking ? <Eye className="w-4 h-4 text-red-400" /> : <EyeOff className="w-4 h-4" style={{ color: '#ffc870' }} />}
            <span className="text-[11px] font-bold tracking-wider"
              style={{ color: isGuardLooking ? '#ff7878' : '#ffc870', textShadow: isGuardLooking ? '0 0 8px rgba(239,68,68,0.5), 0 1px 2px rgba(0,0,0,0.9)' : '0 1px 2px rgba(0,0,0,0.9), 0 0 5px rgba(255,140,0,0.4)' }}>
              {isGuardLooking ? 'GUARD: WATCHING' : 'GUARD: DISTRACTED'}
            </span>
          </motion.div>

          {/* Direction hint */}
          <motion.div className="flex items-center gap-1 mb-3"
            animate={{ x: expectedDirection === 'right' ? [0, 5, 0] : [0, -5, 0] }}
            transition={{ duration: 1.2, repeat: Infinity }}
            style={{ fontFamily: "'Courier New', monospace" }}>
            {expectedDirection === 'left' && <ChevronLeft className="w-4 h-4" style={{ color: '#ffc870' }} />}
            <span className="text-[11px] tracking-wider font-bold" style={{ color: '#ffc870', textShadow: '0 1px 2px rgba(0,0,0,0.9), 0 0 5px rgba(255,140,0,0.4)' }}>// DRAG {expectedDirection.toUpperCase()}</span>
            {expectedDirection === 'right' && <ChevronRight className="w-4 h-4" style={{ color: '#ffc870' }} />}
          </motion.div>

          {/* Dial — Lore-themed: brass double-ring with carved Roman numerals */}
          <div className="flex justify-center mb-4">
            <motion.div
              onMouseDown={handleDialMouseDown}
              onTouchStart={handleDialMouseDown}
              className={`relative w-48 h-48 sm:w-56 sm:h-56 rounded-full cursor-grab active:cursor-grabbing transition-opacity duration-200
                ${isGuardLooking ? 'opacity-20 pointer-events-none' : ''}`}
              style={{
                // Carved-stone outer rim with brass inlay
                background: `
                  radial-gradient(circle at 50% 50%, rgba(20,12,4,0.95) 0%, rgba(8,5,2,0.98) 70%, rgba(0,0,0,1) 100%)
                `,
                boxShadow: `
                  inset 0 0 24px rgba(0,0,0,0.95),
                  inset 0 0 8px ${currentConfig.color}18,
                  0 0 0 2px rgba(140,100,40,0.55),
                  0 0 0 5px rgba(20,12,5,0.95),
                  0 0 0 6px rgba(180,140,55,0.55),
                  0 0 0 9px rgba(40,26,10,0.7),
                  0 4px 18px rgba(0,0,0,0.75),
                  0 0 ${resistance > 50 ? resistance / 2.5 : 22}px ${currentConfig.color}${resistance > 50 ? '60' : '20'}
                `,
                transition: 'box-shadow 0.25s ease',
              }}
              data-testid="dial"
            >
              {/* Carved Roman numerals at cardinal positions — skip 0° to give pointer breathing room */}
              {[
                { val: 'X', deg: 60 },
                { val: 'XX', deg: 120 },
                { val: 'L', deg: 180 },
                { val: 'LXX', deg: 240 },
                { val: 'XC', deg: 300 },
              ].map((n, i) => (
                <div key={i} className="absolute font-bold pointer-events-none" style={{
                  fontFamily: "'Cinzel', serif",
                  fontSize: '0.7rem',
                  letterSpacing: '0.08em',
                  color: 'rgba(200,150,60,0.85)',
                  textShadow: `0 0 6px ${currentConfig.color}40, 0 1px 1px rgba(0,0,0,0.9)`,
                  left: '50%', top: '50%',
                  transformOrigin: '50% 50%',
                  transform: `translate(-50%,-50%) rotate(${n.deg}deg) translateY(-${78}px) rotate(-${n.deg}deg)`,
                }}>
                  {n.val}
                </div>
              ))}

              {/* 24 tick marks — major (every 6th) brass-glow, minor dimmer */}
              {[...Array(24)].map((_, i) => (
                <div key={i} className="absolute pointer-events-none" style={{
                  width: i % 6 === 0 ? '2px' : '1px',
                  height: i % 6 === 0 ? '14px' : '6px',
                  left: '50%', top: '8px',
                  transformOrigin: `50% ${108}px`,
                  transform: `translateX(-50%) rotate(${i * 15}deg)`,
                  background: i % 6 === 0
                    ? 'rgba(220,170,70,0.85)'
                    : 'rgba(160,120,50,0.35)',
                  boxShadow: i % 6 === 0 ? `0 0 5px ${currentConfig.color}55` : 'none',
                }} />
              ))}

              {/* Inner sigil ring — runic engraving around chamber */}
              <div className="absolute pointer-events-none" style={{
                inset: '24%',
                borderRadius: '50%',
                border: '1px dashed rgba(180,140,55,0.4)',
                boxShadow: `inset 0 0 14px ${currentConfig.color}18`,
              }} />
              <div className="absolute pointer-events-none" style={{
                inset: '32%',
                borderRadius: '50%',
                border: '1px solid rgba(180,140,55,0.55)',
              }} />

              {/* Ornate brass pointer — pivots cleanly around dial center via inset-0 wrapper */}
              <motion.div
                className="absolute inset-0 pointer-events-none flex items-start justify-center"
                style={{ transformOrigin: '50% 50%', paddingTop: 6 }}
                animate={{ rotate: dialRotation }}
                transition={{ type: 'tween', duration: 0.05 }}
              >
                <svg width="18" height="60" viewBox="0 0 18 60" style={{
                  filter: `drop-shadow(0 0 10px ${currentConfig.color}80) drop-shadow(0 0 18px ${currentConfig.color}40)`,
                }}>
                  {/* Pointer blade — slim spear */}
                  <path
                    d="M 9 0 L 14 18 L 12 56 L 9 60 L 6 56 L 4 18 Z"
                    fill={`url(#pgrad-${currentStage})`}
                    stroke={currentConfig.color}
                    strokeWidth="0.8"
                  />
                  {/* Notch in blade — sigil mark */}
                  <circle cx="9" cy="36" r="2" fill="rgba(20,12,4,0.95)" stroke={currentConfig.color} strokeWidth="0.6" />
                  <defs>
                    <linearGradient id={`pgrad-${currentStage}`} x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="rgba(255,230,150,0.95)" />
                      <stop offset="40%" stopColor={currentConfig.color} />
                      <stop offset="100%" stopColor="rgba(60,30,10,0.95)" />
                    </linearGradient>
                  </defs>
                </svg>
              </motion.div>

              {/* Center sigil chamber — engraved bezel with dial value */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-16 h-16 rounded-full flex items-center justify-center relative" style={{
                  background: `radial-gradient(circle at 50% 40%, rgba(255,170,50,0.10) 0%, rgba(0,0,0,0.85) 70%)`,
                  border: `1px solid ${currentConfig.color}55`,
                  boxShadow: `inset 0 0 14px ${currentConfig.color}25, 0 0 8px ${currentConfig.color}30`,
                }}>
                  {/* Inner bezel ring */}
                  <div className="absolute pointer-events-none" style={{
                    inset: '4px',
                    borderRadius: '50%',
                    border: '1px solid rgba(180,140,55,0.45)',
                  }} />
                  <span className="font-black text-base" style={{
                    color: currentConfig.color,
                    fontFamily: "'Cinzel', serif",
                    textShadow: `0 0 10px ${currentConfig.color}, 0 1px 2px rgba(0,0,0,0.9)`,
                    letterSpacing: '0.05em',
                  }}>
                    {Math.round(dialValue)}
                  </span>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Resistance */}
          <div className="w-full max-w-xs mb-3">
            <div className="flex justify-between mb-1" style={{ fontFamily: "'Courier New', monospace" }}>
              <span className="text-[10px] uppercase tracking-widest font-bold" style={{ color: '#ffc870', textShadow: '0 1px 2px rgba(0,0,0,0.9)' }}>RESISTANCE</span>
              <span className="text-[11px] font-bold" style={{ color: resistance > 80 ? currentConfig.color : '#ffd86b', textShadow: '0 1px 2px rgba(0,0,0,0.9)' }}>{resistance}%</span>
            </div>
            <div className="h-1.5 overflow-hidden" style={{ background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(255,140,0,0.08)', borderRadius: '1px' }}>
              <motion.div className="h-full" style={{ background: `linear-gradient(90deg, ${currentConfig.color}80, ${currentConfig.color})`, boxShadow: resistance > 70 ? `0 0 10px ${currentConfig.color}60` : 'none' }}
                animate={{ width: `${resistance}%` }} transition={{ duration: 0.15 }} />
            </div>
          </div>

          {/* Messages */}
          <AnimatePresence>
            {showAlarm && (
              <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                className="mt-2 px-4 py-2" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '2px' }}>
                <span className="text-red-400 text-sm font-bold" style={{ fontFamily: "'Courier New', monospace" }}>! {crackingMessage}</span>
              </motion.div>
            )}
          </AnimatePresence>
          <AnimatePresence>
            {crackingMessage && !showAlarm && (
              <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
                className="mt-2 px-4 py-2" style={{ background: `${currentConfig.color}08`, border: `1px solid ${currentConfig.color}25`, borderRadius: '2px' }}>
                <span className="font-bold text-sm" style={{ color: currentConfig.color, fontFamily: "'Courier New', monospace" }}>{crackingMessage}</span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <TotalFeesCounter fees={totalReward} color="#ff8c00" />
        <StatusBar text={`LAYER ${currentStage + 1}/3 // TOLERANCE: ${currentConfig.tolerance}`} color="#ff8c00" />
      </div>
    );
  }

  // ============ WON STATE ============
  if (gameState === 'won') {
    return (
      <div className="relative h-screen w-full select-none overflow-hidden" data-testid="safe-cracker-game"
        style={{ background: 'radial-gradient(ellipse at 50% 70%, #0d0a05 0%, #0a0700 40%, #050300 100%)' }}>
        <style>{CINEMATIC_KEYFRAMES}</style>
        <ScanlineOverlay />
        <GasParticles count={12} color="molten" />
        <div className="relative z-20 flex flex-col items-center justify-center h-full px-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-md text-center" data-testid="safe-cracker-won">
            <div className="p-8 mb-6" style={{ background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(255,140,0,0.2)', borderRadius: '2px' }}>
              <Lock className="w-10 h-10 mx-auto mb-3" style={{ color: '#ff8c00', filter: 'drop-shadow(0 0 10px rgba(255,140,0,0.5))' }} />
              <h3 className="text-2xl font-black mb-2" style={{ color: '#ff8c00', fontFamily: "'Courier New', monospace", textShadow: '0 0 15px rgba(255,140,0,0.3)' }}>
                VAULT BREACHED
              </h3>
              <p className="text-3xl font-black mt-2" style={{ color: '#ff8c00', fontFamily: "'Courier New', monospace", textShadow: '0 0 20px rgba(255,140,0,0.4)' }}>
                +{totalReward.toLocaleString()} FEES
              </p>
              <p className="text-sm mt-1" style={{ color: '#ff6b00', fontFamily: "'Courier New', monospace" }}>+20 Season Points</p>
              {stakerMultiplier > 1 && <p className="text-sm mt-1" style={{ color: 'rgba(255,140,0,0.3)', fontFamily: "'Courier New', monospace" }}>{stakerMultiplier}x bonus applied</p>}
            </div>
            {connected && totalReward > 0 && (
              <div className="mb-4">
                {feesClaimed ? (
                  <div className="py-3" style={{ background: 'rgba(255,140,0,0.05)', border: '1px solid rgba(255,140,0,0.2)', borderRadius: '2px' }}>
                    <p className="font-bold text-sm flex items-center justify-center gap-2" style={{ color: '#ff8c00', fontFamily: "'Courier New', monospace" }}>
                      <Flame className="w-4 h-4" /> +{totalReward.toLocaleString()} FEES CLAIMED
                    </p>
                    <p className="text-[10px] mt-1" style={{ color: '#ff6b00', fontFamily: "'Courier New', monospace" }}>+20 Season Points earned</p>
                    {onchainReceipt && (
                      <div className="mt-3 flex justify-center">
                        <OnChainReceipt receipt={onchainReceipt} />
                      </div>
                    )}
                  </div>
                ) : (
                  <button onClick={claimFees} disabled={claiming} data-testid="claim-fees-btn"
                    className="w-full py-3.5 font-bold transition-all flex items-center justify-center gap-2 text-base"
                    style={{ background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(255,140,0,0.4)', color: '#ff8c00', borderRadius: '2px', fontFamily: "'Courier New', monospace" }}>
                    {claiming ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Flame className="w-5 h-5" /> CLAIM {totalReward.toLocaleString()} FEES</>}
                  </button>
                )}
              </div>
            )}
            {!connected && totalReward > 0 && (
              <div className="py-3 mb-4" style={{ background: 'rgba(234,179,8,0.05)', border: '1px solid rgba(234,179,8,0.15)', borderRadius: '2px' }}>
                <p className="text-sm" style={{ color: 'rgba(234,179,8,0.7)', fontFamily: "'Courier New', monospace" }}>Connect wallet to claim fees</p>
              </div>
            )}
            <div className="flex gap-3">
              <button onClick={() => setGameState('menu')} className="flex-1 py-3 font-medium transition-all"
                style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.4)', borderRadius: '2px', fontFamily: "'Courier New', monospace" }}>
                MENU
              </button>
              <button onClick={startGame} data-testid="play-again-btn"
                className="flex-1 py-3 font-medium transition-all flex items-center justify-center gap-2"
                style={{ background: 'rgba(255,140,0,0.05)', border: '1px solid rgba(255,140,0,0.2)', color: 'rgba(255,140,0,0.7)', borderRadius: '2px', fontFamily: "'Courier New', monospace" }}>
                <RotateCcw className="w-4 h-4" /> AGAIN
              </button>
            </div>
          </motion.div>
        </div>
        <StatusBar text="VAULT CRACKED // ALL LAYERS BREACHED" color="#ff8c00" />
      </div>
    );
  }

  // ============ CAUGHT STATE ============
  if (gameState === 'caught') {
    return (
      <div className="relative h-screen w-full select-none overflow-hidden" data-testid="safe-cracker-game"
        style={{
          background: 'radial-gradient(ellipse at 50% 40%, #1a0000 0%, #0a0000 50%, #000000 100%)',
          animation: caughtPhase === 'flash' ? 'screenShake 0.6s ease-out' : 'none',
        }}>
        <style>{CINEMATIC_KEYFRAMES}{CAUGHT_KEYFRAMES}</style>

        {/* === PHASE 1: CINEMATIC FLASH === */}
        {caughtPhase === 'flash' && (
          <>
            {/* White → Red impact flash */}
            <div style={{
              position: 'absolute', inset: 0, zIndex: 100,
              animation: 'impactFlash 0.8s ease-out forwards',
              pointerEvents: 'none',
            }} />

            {/* Manga-style radial speed lines */}
            <div style={{
              position: 'absolute', inset: '-50%', zIndex: 45,
              animation: 'mangaLines 1.2s ease-out forwards',
              pointerEvents: 'none',
            }}>
              {[...Array(36)].map((_, i) => (
                <div key={i} style={{
                  position: 'absolute',
                  top: '50%', left: '50%',
                  width: '200%', height: '3px',
                  background: `linear-gradient(90deg, transparent 0%, transparent 35%, rgba(255,0,0,${0.4 + Math.random() * 0.3}) 50%, transparent 65%, transparent 100%)`,
                  transformOrigin: '0% 50%',
                  transform: `rotate(${i * 10}deg)`,
                }} />
              ))}
            </div>

            {/* Anime exclamation marks */}
            <div style={{
              position: 'absolute', top: '8%', right: '12%', zIndex: 90,
              fontSize: '120px', fontWeight: 900, color: '#ff0000',
              fontFamily: "'Impact', 'Arial Black', sans-serif",
              animation: 'exclamationBounce 0.6s 0.15s cubic-bezier(0.34, 1.56, 0.64, 1) both',
              textShadow: '0 0 30px #ff0000, 0 0 60px #ff000080, 4px 4px 0px #000',
              WebkitTextStroke: '3px #800000',
              lineHeight: 1,
            }}>!</div>
            <div style={{
              position: 'absolute', top: '12%', right: '8%', zIndex: 90,
              fontSize: '80px', fontWeight: 900, color: '#ff3333',
              fontFamily: "'Impact', 'Arial Black', sans-serif",
              animation: 'exclamationBounce 0.5s 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) both',
              textShadow: '0 0 20px #ff0000, 3px 3px 0px #000',
              WebkitTextStroke: '2px #800000',
              lineHeight: 1,
            }}>!</div>

            {/* ═══ CHAINED SOVEREIGN — pure CSS lore scene (no images) ═══ */}
            {/* Stone wall crack overlay */}
            <div style={{
              position: 'absolute', inset: 0, zIndex: 40,
              backgroundImage: `
                linear-gradient(105deg, transparent 48%, rgba(255,40,40,0.18) 48.5%, rgba(255,40,40,0.18) 49%, transparent 49.5%),
                linear-gradient(75deg, transparent 38%, rgba(255,40,40,0.15) 38.4%, rgba(255,40,40,0.15) 38.7%, transparent 39%),
                linear-gradient(120deg, transparent 62%, rgba(255,40,40,0.12) 62.5%, transparent 63%)
              `,
              animation: 'stonecrackFlash 1.6s ease-out 0.2s both',
              pointerEvents: 'none',
            }} />

            {/* Chained Sovereign Sigil — center stage */}
            <div style={{
              position: 'absolute', inset: 0, zIndex: 50,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              pointerEvents: 'none',
            }}>
              <div style={{
                position: 'relative',
                width: 'min(58vh, 380px)',
                height: 'min(58vh, 380px)',
                animation: 'sovereignWake 1s cubic-bezier(0.22, 1, 0.36, 1) forwards',
              }}>
                {/* Outer rune ring — slow rotate */}
                <div style={{
                  position: 'absolute', inset: 0,
                  borderRadius: '50%',
                  border: '2px solid rgba(255,40,40,0.45)',
                  boxShadow: '0 0 40px rgba(255,0,0,0.4), inset 0 0 30px rgba(180,0,0,0.3)',
                  animation: 'runeRingSpin 14s linear infinite',
                }}>
                  {[...Array(12)].map((_, i) => (
                    <div key={i} style={{
                      position: 'absolute', left: '50%', top: '-2px',
                      width: i % 3 === 0 ? '2px' : '1px',
                      height: i % 3 === 0 ? '14px' : '7px',
                      transformOrigin: '50% calc(50% + 1px)',
                      transform: `translateX(-50%) rotate(${i * 30}deg)`,
                      background: 'rgba(255,40,40,0.7)',
                      boxShadow: '0 0 6px rgba(255,40,40,0.6)',
                    }} />
                  ))}
                </div>

                {/* Inner rune ring — counter-rotate */}
                <div style={{
                  position: 'absolute', inset: '14%',
                  borderRadius: '50%',
                  border: '1px dashed rgba(255,80,80,0.5)',
                  animation: 'runeRingSpinRev 9s linear infinite',
                }} />
                <div style={{
                  position: 'absolute', inset: '22%',
                  borderRadius: '50%',
                  border: '1px solid rgba(180,0,0,0.55)',
                  boxShadow: 'inset 0 0 20px rgba(255,0,0,0.4)',
                }} />

                {/* The Sovereign's Eye — iris layer */}
                <div style={{
                  position: 'absolute', inset: '30%',
                  borderRadius: '50%',
                  background: 'radial-gradient(circle at 50% 50%, rgba(255,200,180,0.9) 0%, rgba(255,80,40,0.85) 18%, rgba(220,20,0,0.95) 38%, rgba(80,0,0,1) 70%, rgba(20,0,0,1) 100%)',
                  boxShadow: '0 0 60px rgba(255,40,40,0.7), inset 0 0 30px rgba(0,0,0,0.6)',
                }}>
                  {/* Iris striations */}
                  {[...Array(16)].map((_, i) => (
                    <div key={i} style={{
                      position: 'absolute', left: '50%', top: '50%',
                      width: '2px', height: '40%',
                      background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.55) 30%, rgba(0,0,0,0.7) 100%)',
                      transformOrigin: '50% 0%',
                      transform: `translate(-50%, 0) rotate(${i * 22.5}deg)`,
                      opacity: 0.7,
                    }} />
                  ))}
                </div>

                {/* The Pupil — vertical slit (gremlin/dragon-esque) */}
                <div style={{
                  position: 'absolute', inset: '42%',
                  borderRadius: '50% / 100%',
                  background: 'radial-gradient(ellipse at 50% 50%, #000 0%, #100000 70%, #1a0000 100%)',
                  border: '1px solid rgba(255,0,0,0.6)',
                  boxShadow: '0 0 20px rgba(255,0,0,0.8), inset 0 0 12px rgba(255,40,40,0.4)',
                  animation: 'ironPupilDilate 1.4s 0.4s cubic-bezier(0.22, 1, 0.36, 1) both',
                }} />

                {/* Pupil reflection highlight */}
                <div style={{
                  position: 'absolute', top: '44%', left: '47%',
                  width: '8px', height: '8px',
                  borderRadius: '50%',
                  background: 'rgba(255,220,200,0.85)',
                  boxShadow: '0 0 8px rgba(255,200,150,0.7)',
                  animation: 'ironPupilDilate 1.4s 0.5s cubic-bezier(0.22, 1, 0.36, 1) both',
                }} />

                {/* Aura — radial red bleed */}
                <div style={{
                  position: 'absolute', inset: '-30%',
                  background: 'radial-gradient(circle, rgba(255,0,0,0.35) 0%, rgba(255,0,0,0.15) 30%, transparent 60%)',
                  zIndex: -1,
                  borderRadius: '50%',
                }} />
              </div>
            </div>

            {/* Dangling chains — left + right swinging in */}
            <div style={{
              position: 'absolute', top: '0%', left: '50%',
              zIndex: 55, pointerEvents: 'none',
              transformOrigin: '50% -40px',
              animation: 'chainSwingLeft 0.9s cubic-bezier(0.34, 1.2, 0.64, 1) forwards',
              marginLeft: 'min(-22vh, -150px)',
            }}>
              {/* Iron chain — column of links */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>
                {[...Array(14)].map((_, i) => (
                  <div key={i} style={{
                    width: i % 2 === 0 ? '14px' : '10px',
                    height: '20px',
                    border: '2px solid rgba(120,90,40,0.85)',
                    borderRadius: i % 2 === 0 ? '50% 50% 50% 50% / 60% 60% 40% 40%' : '40% 40% 50% 50% / 50% 50% 60% 60%',
                    background: 'linear-gradient(135deg, rgba(70,50,20,0.9) 0%, rgba(30,20,8,1) 100%)',
                    boxShadow: 'inset 0 1px 1px rgba(200,160,80,0.3), 0 1px 2px rgba(0,0,0,0.7)',
                    marginTop: i === 0 ? 0 : '-6px',
                    transform: i % 2 === 0 ? 'rotate(0deg)' : 'rotate(90deg)',
                  }} />
                ))}
                {/* Iron cuff at the bottom */}
                <div style={{
                  width: '46px', height: '18px',
                  marginTop: '4px',
                  borderRadius: '4px',
                  background: 'linear-gradient(180deg, rgba(110,80,30,0.95) 0%, rgba(50,32,10,1) 100%)',
                  border: '1px solid rgba(140,100,40,0.7)',
                  boxShadow: 'inset 0 2px 2px rgba(220,180,90,0.3), inset 0 -2px 2px rgba(0,0,0,0.7), 0 2px 4px rgba(0,0,0,0.7)',
                }} />
              </div>
            </div>

            <div style={{
              position: 'absolute', top: '0%', left: '50%',
              zIndex: 55, pointerEvents: 'none',
              transformOrigin: '50% -40px',
              animation: 'chainSwingRight 0.9s cubic-bezier(0.34, 1.2, 0.64, 1) forwards',
              marginLeft: 'min(20vh, 140px)',
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>
                {[...Array(14)].map((_, i) => (
                  <div key={i} style={{
                    width: i % 2 === 0 ? '14px' : '10px',
                    height: '20px',
                    border: '2px solid rgba(120,90,40,0.85)',
                    borderRadius: i % 2 === 0 ? '50% 50% 50% 50% / 60% 60% 40% 40%' : '40% 40% 50% 50% / 50% 50% 60% 60%',
                    background: 'linear-gradient(135deg, rgba(70,50,20,0.9) 0%, rgba(30,20,8,1) 100%)',
                    boxShadow: 'inset 0 1px 1px rgba(200,160,80,0.3), 0 1px 2px rgba(0,0,0,0.7)',
                    marginTop: i === 0 ? 0 : '-6px',
                    transform: i % 2 === 0 ? 'rotate(0deg)' : 'rotate(90deg)',
                  }} />
                ))}
                <div style={{
                  width: '46px', height: '18px',
                  marginTop: '4px',
                  borderRadius: '4px',
                  background: 'linear-gradient(180deg, rgba(110,80,30,0.95) 0%, rgba(50,32,10,1) 100%)',
                  border: '1px solid rgba(140,100,40,0.7)',
                  boxShadow: 'inset 0 2px 2px rgba(220,180,90,0.3), inset 0 -2px 2px rgba(0,0,0,0.7), 0 2px 4px rgba(0,0,0,0.7)',
                }} />
              </div>
            </div>

            {/* Ember sparks — flying outward from center */}
            {[...Array(20)].map((_, i) => {
              const angle = (i / 20) * Math.PI * 2;
              const dist = 200 + Math.random() * 200;
              return (
                <div key={i} style={{
                  position: 'absolute', top: '50%', left: '50%',
                  width: '4px', height: '4px',
                  borderRadius: '50%',
                  background: i % 3 === 0 ? '#ffd700' : '#ff4500',
                  boxShadow: `0 0 8px ${i % 3 === 0 ? '#ffd700' : '#ff4500'}`,
                  '--dx': `${Math.cos(angle) * dist}px`,
                  '--dy': `${Math.sin(angle) * dist}px`,
                  animation: `emberSpark ${0.8 + Math.random() * 0.6}s ${0.2 + Math.random() * 0.3}s cubic-bezier(0.22, 1, 0.36, 1) forwards`,
                  zIndex: 45,
                  pointerEvents: 'none',
                }} />
              );
            })}

            {/* BUSTED text slam */}
            <div style={{
              position: 'absolute', bottom: '12%', left: 0, right: 0, zIndex: 80,
              textAlign: 'center',
            }}>
              <div style={{
                fontSize: 'clamp(48px, 12vw, 100px)',
                fontWeight: 900,
                color: '#ff0000',
                fontFamily: "'Impact', 'Arial Black', sans-serif",
                animation: 'bustedSlam 0.7s 0.2s cubic-bezier(0.22, 1, 0.36, 1) both',
                textShadow: '0 0 20px #ff0000, 0 0 60px #ff000060, 0 4px 0 #800000, 0 8px 0 #400000',
                WebkitTextStroke: '2px #aa0000',
                letterSpacing: '0.2em',
              }}>
                BUSTED
              </div>
              <div style={{
                fontSize: 'clamp(12px, 2.5vw, 18px)',
                color: 'rgba(255,100,100,0.7)',
                fontFamily: "'Courier New', monospace",
                letterSpacing: '0.3em',
                animation: 'bustedSlam 0.6s 0.5s cubic-bezier(0.22, 1, 0.36, 1) both',
                textTransform: 'uppercase',
              }}>
                {crackingMessage || 'The Sovereign has seen you'}
              </div>
            </div>

            {/* VHS tracking lines */}
            {[...Array(3)].map((_, i) => (
              <div key={i} style={{
                position: 'absolute',
                top: `${20 + i * 30}%`,
                left: 0, width: '100%', height: '4px',
                background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 40%, rgba(255,255,255,0.15) 60%, transparent 100%)',
                animation: `vhsTrack ${0.3 + i * 0.15}s ${i * 0.2}s linear forwards`,
                zIndex: 95, pointerEvents: 'none',
                opacity: 0.6,
              }} />
            ))}
          </>
        )}

        {/* === PHASE 2: RESULTS === */}
        <AnimatePresence>
          {caughtPhase === 'results' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="absolute inset-0 z-30 flex flex-col items-center justify-center"
            >
              <ScanlineOverlay />
              <GasParticles count={8} color="molten" />

              {/* Animated border frame */}
              <div className="absolute inset-3 pointer-events-none" style={{
                border: '2px solid rgba(255,0,0,0.6)',
                animation: 'borderFlash 2s ease-in-out infinite',
              }} />

              {/* Sovereign Sigil — small lore-themed card */}
              <motion.div
                initial={{ y: -30, opacity: 0, scale: 0.8 }}
                animate={{ y: 0, opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, type: 'spring', stiffness: 120 }}
                className="mb-4 relative"
                data-testid="sovereign-sigil-card"
                style={{ width: 'min(40vw, 160px)', height: 'min(40vw, 160px)' }}
              >
                {/* Outer rune ring */}
                <div style={{
                  position: 'absolute', inset: 0,
                  borderRadius: '50%',
                  border: '1.5px solid rgba(255,40,40,0.45)',
                  boxShadow: '0 0 22px rgba(255,0,0,0.35), inset 0 0 18px rgba(180,0,0,0.25)',
                  animation: 'runeRingSpin 16s linear infinite',
                }}>
                  {[...Array(8)].map((_, i) => (
                    <div key={i} style={{
                      position: 'absolute', left: '50%', top: '-1px',
                      width: '1px', height: '8px',
                      transformOrigin: '50% calc(50% + 1px)',
                      transform: `translateX(-50%) rotate(${i * 45}deg)`,
                      background: 'rgba(255,40,40,0.65)',
                      boxShadow: '0 0 4px rgba(255,40,40,0.55)',
                    }} />
                  ))}
                </div>

                {/* Eye iris */}
                <div style={{
                  position: 'absolute', inset: '22%',
                  borderRadius: '50%',
                  background: 'radial-gradient(circle at 50% 50%, rgba(255,200,180,0.85) 0%, rgba(255,80,40,0.85) 18%, rgba(220,20,0,0.95) 38%, rgba(80,0,0,1) 70%, rgba(20,0,0,1) 100%)',
                  boxShadow: '0 0 30px rgba(255,40,40,0.55), inset 0 0 16px rgba(0,0,0,0.6)',
                }}>
                  {[...Array(12)].map((_, i) => (
                    <div key={i} style={{
                      position: 'absolute', left: '50%', top: '50%',
                      width: '1.5px', height: '40%',
                      background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.5) 30%, rgba(0,0,0,0.7) 100%)',
                      transformOrigin: '50% 0%',
                      transform: `translate(-50%, 0) rotate(${i * 30}deg)`,
                      opacity: 0.7,
                    }} />
                  ))}
                </div>

                {/* Pupil — vertical slit */}
                <div style={{
                  position: 'absolute', top: '38%', left: '47%',
                  width: '6%', height: '24%',
                  borderRadius: '50% / 100%',
                  background: 'radial-gradient(ellipse at 50% 50%, #000 0%, #100000 70%)',
                  border: '1px solid rgba(255,0,0,0.5)',
                  boxShadow: '0 0 10px rgba(255,0,0,0.6)',
                }} />
              </motion.div>

              {/* ALERT header */}
              <motion.div
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.15, type: 'spring', stiffness: 200 }}
                className="text-center mb-4"
              >
                <div style={{
                  fontSize: 'clamp(28px, 7vw, 48px)',
                  fontWeight: 900,
                  color: '#ff0000',
                  fontFamily: "'Impact', 'Arial Black', sans-serif",
                  animation: 'alertPulse 2s ease-in-out infinite',
                  letterSpacing: '0.15em',
                  WebkitTextStroke: '1px #aa0000',
                }}>
                  DETECTED
                </div>
                <div style={{
                  fontSize: '12px', color: 'rgba(255,80,80,0.5)',
                  fontFamily: "'Courier New', monospace",
                  letterSpacing: '0.2em', marginTop: '4px',
                }}>
                  {'// '}{crackingMessage || 'Security alert triggered'}
                </div>
              </motion.div>

              {/* Stats box */}
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.4 }}
                className="w-full max-w-sm px-4"
                data-testid="safe-cracker-caught"
              >
                <div className="p-5 mb-4 text-center" style={{
                  background: 'rgba(0,0,0,0.7)',
                  border: '1px solid rgba(239,68,68,0.25)',
                  borderRadius: '2px',
                  backdropFilter: 'blur(10px)',
                }}>
                  <p className="text-sm" style={{ color: 'rgba(255,255,255,0.3)', fontFamily: "'Courier New', monospace" }}>
                    LAYERS CRACKED: <span style={{ color: 'rgba(239,68,68,0.7)' }}>{completedCount}/3</span>
                  </p>
                  <p className="text-base font-black mt-3 tracking-widest" style={{
                    color: '#ef4444',
                    fontFamily: "'Courier New', monospace",
                    textShadow: '0 0 10px rgba(239,68,68,0.4)',
                  }}>
                    ALL FEES LOST
                  </p>
                  <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.2)', fontFamily: "'Courier New', monospace" }}>
                    Breach all 3 layers to claim fees
                  </p>
                </div>

                {/* Action buttons */}
                <div className="flex gap-3">
                  <button onClick={() => setGameState('menu')} className="flex-1 py-3 font-medium transition-all"
                    style={{
                      background: 'rgba(0,0,0,0.4)',
                      border: '1px solid rgba(255,255,255,0.06)',
                      color: 'rgba(255,255,255,0.4)',
                      borderRadius: '2px',
                      fontFamily: "'Courier New', monospace",
                    }}>
                    MENU
                  </button>
                  <button onClick={startGame} data-testid="try-again-btn"
                    className="flex-1 py-3 font-medium transition-all flex items-center justify-center gap-2"
                    style={{
                      background: 'rgba(239,68,68,0.08)',
                      border: '1px solid rgba(239,68,68,0.3)',
                      color: 'rgba(239,68,68,0.8)',
                      borderRadius: '2px',
                      fontFamily: "'Courier New', monospace",
                    }}>
                    <RotateCcw className="w-4 h-4" /> RETRY
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <StatusBar text="ALARM TRIGGERED // SECURITY BREACH DETECTED" color="#ef4444" />
      </div>
    );
  }

  return null;
}
